import {
  Step,
  Stepper as MuiStepper,
  type StepperProps,
  StepButton,
  Box,
  StepLabel,
} from "@mui/material";
import { Connector } from "Components/Shared/Stepper/Connector";
import { StepIcon } from "Components/Shared/Stepper/StepIcon";
import type { StepData } from "Components/Shared/Stepper/types";
import {
  canStepBeActivated,
  getActiveConnectors,
} from "Components/Shared/Stepper/utils";
import { useIsMobile } from "Hooks/useIsMobile";

type Props<TStepId extends string = string> = {
  steps: ReadonlyArray<StepData<TStepId>>;
  activeStepIndex?: number;
  onStepClick: (step: number) => void;
} & StepperProps;

export const Stepper = <TStepId extends string = string>({
  steps,
  activeStepIndex,
  onStepClick,
  ...props
}: Props<TStepId>) => {
  const activeConnectors = getActiveConnectors(steps, activeStepIndex ?? 0);
  const isMobile = useIsMobile();

  return (
    <MuiStepper
      alternativeLabel
      activeStep={activeStepIndex}
      connector={null} // We're rendering connectors manually
      {...props}
    >
      {steps.map(({ id, label, isCompleted, icon }, index) => {
        const canActivateStep = canStepBeActivated(steps, index);
        const isCurrentlyActive = activeStepIndex === index;

        const stepContent = isMobile ? (
          isCurrentlyActive ? (
            label
          ) : (
            <Box sx={{ color: "text.secondary", height: "100%" }}>{icon}</Box>
          )
        ) : (
          label
        );

        return (
          <Step key={id} completed={isCompleted}>
            {index > 0 && <Connector isActive={activeConnectors[index]} />}
            <StepButton
              color="inherit"
              onClick={() => canActivateStep && onStepClick(index)}
              sx={({ spacing }) => ({
                cursor: canActivateStep ? "pointer" : "default",
                padding: spacing(6, 3),
                margin: spacing(-6, -3.5),
              })}
            >
              <StepLabel
                slots={{ stepIcon: StepIcon }}
                sx={{
                  "& .MuiStepLabel-label.MuiStepLabel-alternativeLabel": {
                    mt: 0,
                    fontSize: "0.875rem",
                    fontWeight: isCurrentlyActive ? 600 : 400,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: "25px",
                  }}
                >
                  {stepContent}
                </Box>
              </StepLabel>
            </StepButton>
          </Step>
        );
      })}
    </MuiStepper>
  );
};
